/* eslint-disable react/prop-types */
import { useState } from "react";

import {
  alignItems,
  colors,
  darkModeColors,
  campaignStatus,
  strCode,
  allDeviceTypeOption
} from "../../../../consts";
import { t } from "../../../../services";
import { dateUtil, deviceUtil, fwuCampaignUtils } from "../../../../utils";

import {
  DeviceInfo,
  FlexContainers,
  GradientLoader,
  SVG,
  TextOverflowWrapper,
  WrapperContainer
} from "../../../../components";
import { CellWrapper } from "../../../../components/Table/styled";
import { EmptyCircle } from "./styled";

import mercuryCharger from "../../../../assets/images/deviceImages/mercury_charger.png";

const deviceFilter = (rows, id, filterValue) => {
  return rows.filter((row) => {
    const value = row.values[id];

    return (
      !value ||
      ((!filterValue.model ||
        filterValue.model.length === 0 ||
        filterValue.model.includes(value)) &&
        (!filterValue.type ||
          filterValue.type === allDeviceTypeOption.id ||
          deviceUtil.getDeviceType(value).includes(filterValue.type)))
    );
  });
};

export const FWUCampaignTableColumns = [
  {
    Header: strCode.CAMPAIGN_NAME_TITLE,
    accessor: "campaignName",
    Cell: ({ cell, cellWidth7, searchedText, isLoading }) => {
      return (
        <CellWrapper width={`${cellWidth7}px`}>
          <TextOverflowWrapper
            style={{
              color: darkModeColors.RAISIN_70
            }}
            highlightedText={searchedText}>
            {isLoading ? (
              <GradientLoader width={180} height={24} animationDuration={24} />
            ) : (
              cell.value
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.DEVICE_TITLE,
    accessor: "deviceModel",
    filter: deviceFilter,
    Cell: ({ cell, isLoading }) => {
      const [isImageLoaded, setIsImageLoaded] = useState(false);

      return (
        <CellWrapper width={`250px`}>
          <DeviceInfo
            isLoading={isLoading}
            isImageLoaded={isImageLoaded}
            setIsImageLoaded={setIsImageLoaded}
            imageSrc={mercuryCharger}
            model={cell.value}
            type={t(strCode.CHARGER_LABEL)}
            width={250}
          />
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.MARKET_TITLE,
    accessor: "markets",
    Cell: ({ cell, cellWidth10, isLoading }) => {
      const markets = cell?.value ?? [];

      return (
        <CellWrapper width={`${cellWidth10}px`}>
          <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
            {isLoading ? (
              <GradientLoader width={60} height={24} animationDuration={40} />
            ) : (
              markets?.join(", ")
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.NEW_FW_VERSION_TITLE,
    accessor: "versionTo",
    Cell: ({ cell, isLoading }) => {
      return (
        <CellWrapper width={`90px`}>
          <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
            {isLoading ? (
              <GradientLoader width={75} height={24} animationDuration={32} />
            ) : (
              cell.value
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.START_DATE_TITLE,
    accessor: "startDate",
    Cell: ({ cell, cellWidth8, isLoading }) => {
      let timestamp = null;

      try {
        const date = dateUtil.getUTCDate(new Date(cell?.value));
        timestamp = date?.getTime();
      } catch (e) {
        timestamp = null;
      }

      return (
        <CellWrapper width={`${cellWidth8}px`}>
          <TextOverflowWrapper style={{ color: colors.RAISIN_70 }}>
            {isLoading ? (
              <GradientLoader width={120} height={24} animationDuration={32} />
            ) : (
              dateUtil.formatDateFromTimestamp(timestamp)
            )}
          </TextOverflowWrapper>
        </CellWrapper>
      );
    }
  },
  {
    Header: strCode.PROGRESS_TITLE,
    id: "status",
    accessor: (record) => {
      const status = record?.status;
      const isScheduled = fwuCampaignUtils.getIsScheduled(record);

      return isScheduled ? campaignStatus.scheduled : status;
    },
    Cell: ({ cell, cellWidth8, isLoading }) => {
      const status = cell.row.original?.status;
      const isActive = status === campaignStatus.active;
      const isInactive = status === campaignStatus.inactive;
      const isScheduled = fwuCampaignUtils.getIsScheduled(cell.row.original);

      const numberOfSucceededThings = cell.row.original?.numberOfSucceededThings;
      const deactivationDate = cell.row.original?.deactivationDate;
      let deactivationDateFormatted = null;

      if (deactivationDate) {
        try {
          const date = dateUtil.getUTCDate(new Date(deactivationDate));
          const timestamp = date?.getTime();

          deactivationDateFormatted = dateUtil.formatDateFromTimestamp(timestamp);
        } catch (e) {
          deactivationDateFormatted = null;
        }
      }

      return (
        <CellWrapper width={`${cellWidth8}px`}>
          {isLoading ? (
            <GradientLoader width={cellWidth8} height={24} animationDuration={24} />
          ) : (
            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={10}>
              {isInactive ? (
                <SVG.FailureFilledIcon fill={colors.RAISIN_40} />
              ) : isScheduled ? (
                <EmptyCircle />
              ) : isActive ? (
                <SVG.SuccessFilledIcon />
              ) : null}

              <WrapperContainer
                Container={FlexContainers.FlexColumnStart}
                alignItems={alignItems.start}
                maxWidth={`${cellWidth8}px`}
                textAlign={"left"}>
                <TextOverflowWrapper
                  maxWidth={`${cellWidth8 - 35}px`}
                  style={{ color: colors.RAISIN_80 }}>
                  {t(
                    isInactive
                      ? strCode.FWU_DEACTIVATED_INFO
                      : isScheduled
                      ? strCode.SCHEDULED_TITLE
                      : isActive
                      ? strCode.FWU_UPDATED_INFO
                      : null,
                    { updated: numberOfSucceededThings }
                  )}
                </TextOverflowWrapper>

                {deactivationDateFormatted && (
                  <TextOverflowWrapper maxWidth={`${cellWidth8 - 35}px`}>
                    <p
                      style={{
                        color: colors.RAISIN_50,
                        fontSize: "14px",
                        whiteSpace: "pre"
                      }}>
                      {deactivationDateFormatted}
                    </p>
                  </TextOverflowWrapper>
                )}
              </WrapperContainer>
            </WrapperContainer>
          )}
        </CellWrapper>
      );
    }
  }
];
